import { Data, DotLottieWorkerReact } from '@lottiefiles/dotlottie-react'
import classNames from 'classnames'
import { useEffect, useMemo, useState } from 'react'

import { DiamondAnimation } from '@shared'
import cornerDownSrc from '../../assets/corner-down.svg'
import cornerUpSrc from '../../assets/corner-up.svg'
import priceBadgeSrc from '../../assets/reward-price-badge.svg'
import lockBadgeSrc from '../../assets/lock-badge.svg'
import { StarAnim } from '../StarAnim'
import styles from './RewardCard.module.scss'

export type RewardCardState = 'claimed' | 'available' | 'locked'

interface RewardCardProps {
  amount: string
  label: string
  state: RewardCardState
  iconUrl?: string
  onClaim?: () => void
}

const getExtension = (value: string): string | null => {
  const match = value.match(/\.([a-z0-9]+)$/i)
  return match ? match[1] : null
}

const SkinLottie = ({ imgUrl }: { imgUrl: string }) => {
  const [data, setData] = useState<Data>()

  useEffect(() => {
    fetch(imgUrl)
      .then((res) => res.arrayBuffer())
      .then((data) => setData(data))
  }, [imgUrl])

  if (!data) {
    return null
  }

  return <DotLottieWorkerReact data={data} loop autoplay className={styles.skinIcon} />
}

const SkinContent = ({ imgUrl, withMask }: { imgUrl: string; withMask: boolean }) => {
  const extension = useMemo(() => getExtension(imgUrl), [imgUrl])

  if (extension === 'lottie') {
    return <SkinLottie imgUrl={imgUrl} />
  }

  if (extension === 'mp4') {
    return (
      <div className={styles.skinVideoWrapper}>
        <video
          src={imgUrl}
          autoPlay
          loop
          muted
          playsInline
          className={classNames(styles.skinIcon, { [styles.skinMask]: withMask })}
        />
      </div>
    )
  }

  return (
    <img
      src={imgUrl}
      className={classNames(styles.skinIcon, { [styles.skinMask]: withMask })}
      alt=""
    />
  )
}

export const RewardCard = ({ amount, label, state, iconUrl, onClaim }: RewardCardProps) => {
  return (
    <div className={classNames(styles.card, styles[state as keyof typeof styles])}>
      <div className={styles.outerGlow} />
      <div className={styles.cardBg} />

      <div className={styles.cornerDown}>
        <img src={cornerDownSrc} alt="" />
      </div>
      <div className={styles.cornerUp}>
        <img src={cornerUpSrc} alt="" />
      </div>

      <div className={styles.starAnimWrap}>
        <StarAnim width={140} height={140} className={styles.starAnim} />
      </div>

      <svg viewBox="0 0 199 183" fill="none" className={styles.diamondLayer}>
        <DiamondAnimation optimized />
      </svg>

      {iconUrl && (
        <SkinContent imgUrl={iconUrl} withMask />
      )}

      <p className={styles.amount}>{amount}</p>
      <p className={styles.label}>{label}</p>

      <div className={styles.badge}>
        {state === 'claimed' && (
          <img src={priceBadgeSrc} alt="" className={styles.priceBadge} />
        )}
        {state === 'available' && (
          <button className={styles.claimButton} onClick={onClaim}>
            claim
          </button>
        )}
        {state === 'locked' && (
          <div className={styles.lockedBadge}>
            <img src={lockBadgeSrc} alt="" className={styles.lockIcon} />
          </div>
        )}
      </div>
    </div>
  )
}
